import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

const DEFAULT_DIMS = {
  simple: { longueur: 2250, largeur: 70 },
  double: { longueur: 2250, largeur: 160 },
  triple: { longueur: 2250, largeur: 360 },
};

function normalizeText(text) {
  return (text ?? "").toString().toLowerCase().normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtQty(n) {
  return (Number(n) || 0).toLocaleString("fr-FR");
}

function fmtVolume(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "—";
  return v.toLocaleString("fr-FR", { minimumFractionDigits: 4, maximumFractionDigits: 6 });
}

function fmtPrix(n) {
  if (n == null || n === "" || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function dimLabel(dims, type) {
  const d = (dims || DEFAULT_DIMS)[type] || DEFAULT_DIMS[type];
  return `${d.longueur} × ${d.largeur} mm`;
}

export default function PlacagesDecoupes() {
  const { ui } = useI18n();

  const [items, setItems] = useState([]);
  const [dims, setDims] = useState(DEFAULT_DIMS);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setErr("");
      try {
        const res = await apiFetch("/api/logistique/placages-decoupes");
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status === "error") {
          throw new Error(data.message || "Erreur chargement");
        }
        if (!cancelled) {
          setItems(Array.isArray(data.items) ? data.items : []);
          if (data.dimensions) setDims(data.dimensions);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const view = useMemo(() => {
    const s = normalizeText(search);
    if (!s) return items;
    return items.filter(
      (r) => normalizeText(r.lot).includes(s) || normalizeText(r.pn).includes(s)
    );
  }, [items, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totals = useMemo(() => {
    return view.reduce(
      (acc, r) => {
        acc.placages += Number(r.placages) || 0;
        acc.ok += Number(r.totalOk) || 0;
        acc.nok += Number(r.totalNok) || 0;
        acc.volumeOk += Number(r.volumeOkM3) || 0;
        acc.volume += Number(r.volumeM3) || 0;
        if (r.prixOk != null && Number.isFinite(Number(r.prixOk))) {
          acc.prixOk += Number(r.prixOk);
        }
        if (r.prixTotal != null && Number.isFinite(Number(r.prixTotal))) {
          acc.prix += Number(r.prixTotal);
        }
        return acc;
      },
      { placages: 0, ok: 0, nok: 0, volumeOk: 0, volume: 0, prixOk: 0, prix: 0 }
    );
  }, [view]);

  const totalPages = Math.max(1, Math.ceil(view.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = view.slice(startIndex, startIndex + itemsPerPage);

  function handleExportExcel() {
    if (!view.length) return;

    const rows = view.map((r) => ({
      "Num de lot": r.lot || "",
      PN: r.pn || "",
      Essence: r.essence || "",
      Couleur: r.couleur || "",
      "Épaisseur (mm)": r.epaisseur ?? "",
      "Placages découpés": Number(r.placages) || 0,
      "Plis simples OK": Number(r.simpleOk) || 0,
      "Plis simples NOK": Number(r.simpleNok) || 0,
      "Dim. simples (mm)": dimLabel(dims, "simple"),
      "Plis doubles OK": Number(r.doubleOk) || 0,
      "Plis doubles NOK": Number(r.doubleNok) || 0,
      "Dim. doubles (mm)": dimLabel(dims, "double"),
      "Plis triples OK": Number(r.tripleOk) || 0,
      "Plis triples NOK": Number(r.tripleNok) || 0,
      "Dim. triples (mm)": dimLabel(dims, "triple"),
      "Total OK": Number(r.totalOk) || 0,
      "Total NOK": Number(r.totalNok) || 0,
      "Volume OK (m³)": Number(r.volumeOkM3) || 0,
      "Volume NOK (m³)": Number(r.volumeNokM3) || 0,
      "Volume total (m³)": Number(r.volumeM3) || 0,
      "Prix / m³ (€)": r.prixMetreCube ?? "",
      "Prix inventaire OK (€)": r.prixOk ?? "",
      "Prix total (€)": r.prixTotal ?? "",
      Modèles: Array.isArray(r.modeles) ? r.modeles.join(", ") : "",
      "Dernière découpe": r.lastDate || "",
    }));

    rows.push({
      "Num de lot": "TOTAL",
      PN: "",
      Essence: "",
      Couleur: "",
      "Épaisseur (mm)": "",
      "Placages découpés": totals.placages,
      "Plis simples OK": "",
      "Plis simples NOK": "",
      "Dim. simples (mm)": "",
      "Plis doubles OK": "",
      "Plis doubles NOK": "",
      "Dim. doubles (mm)": "",
      "Plis triples OK": "",
      "Plis triples NOK": "",
      "Dim. triples (mm)": "",
      "Total OK": totals.ok,
      "Total NOK": totals.nok,
      "Volume OK (m³)": totals.volumeOk,
      "Volume NOK (m³)": "",
      "Volume total (m³)": totals.volume,
      "Prix / m³ (€)": "",
      "Prix inventaire OK (€)": totals.prixOk || "",
      "Prix total (€)": totals.prix || "",
      Modèles: "",
      "Dernière découpe": "",
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch: Math.min(36, Math.max(12, key.length + 2)),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Placages découpés");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `placages_decoupes_${stamp}.xlsx`);
  }

  return (
    <div className="pdv-page">
      <MenuBar active="logistique" />

      <div className="pdv-wrap">
        <div className="pdv-shell">
          <header className="pdv-header">
            <div className="pdv-header-kicker">{ui("Logistique")}</div>
            <h1 className="pdv-header-title">Placages découpés</h1>
            <p className="pdv-header-sub">
              Quantités, volume m³ et prix inventaire des plis découpés, par lot
            </p>
          </header>

          <div className="pdv-body">
            <div className="pdv-stats-bar">
              <div className="pdv-stat-pill">
                <span className="pdv-stat-label">Lots</span>
                <strong className="pdv-stat-value">{view.length}</strong>
              </div>
              <div className="pdv-stat-pill">
                <span className="pdv-stat-label">Placages découpés</span>
                <strong className="pdv-stat-value">{fmtQty(totals.placages)}</strong>
              </div>
              <div className="pdv-stat-pill">
                <span className="pdv-stat-label">Plis OK</span>
                <strong className="pdv-stat-value">{fmtQty(totals.ok)}</strong>
              </div>
              <div className="pdv-stat-pill pdv-stat-nok">
                <span className="pdv-stat-label">Plis NOK</span>
                <strong className="pdv-stat-value">{fmtQty(totals.nok)}</strong>
              </div>
              <div className="pdv-stat-pill">
                <span className="pdv-stat-label">Volume OK (m³)</span>
                <strong className="pdv-stat-value">{fmtVolume(totals.volumeOk)}</strong>
              </div>
              <div className="pdv-stat-pill">
                <span className="pdv-stat-label">Prix inventaire OK</span>
                <strong className="pdv-stat-value">{fmtPrix(totals.prixOk || null)}</strong>
              </div>
            </div>

            <section className="pdv-section">
              <div className="pdv-section-label">Recherche par lot</div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtrer par numéro de lot ou PN…"
                className="pdv-search"
              />
            </section>

            <section className="pdv-section pdv-section-table">
              <div className="pdv-section-head">
                <div className="pdv-section-label">Détail par lot</div>
                <button
                  type="button"
                  className="pdv-export-btn"
                  onClick={handleExportExcel}
                  disabled={loading || view.length === 0}
                >
                  Exporter Excel
                </button>
              </div>

              <div className="pdv-table-wrap">
                <table className="pdv-table">
                  <thead>
                    <tr>
                      <th>Num de lot</th>
                      <th>PN</th>
                      <th>Ép. (mm)</th>
                      <th>Placages découpés</th>
                      <th>
                        Plis simples
                        <span className="pdv-th-dim">{dimLabel(dims, "simple")}</span>
                      </th>
                      <th>
                        Plis doubles
                        <span className="pdv-th-dim">{dimLabel(dims, "double")}</span>
                      </th>
                      <th>
                        Plis triples
                        <span className="pdv-th-dim">{dimLabel(dims, "triple")}</span>
                      </th>
                      <th>Total OK</th>
                      <th>NOK</th>
                      <th>
                        Volume OK
                        <span className="pdv-th-dim">m³</span>
                      </th>
                      <th>
                        Prix / m³
                        <span className="pdv-th-dim">lot</span>
                      </th>
                      <th>
                        Prix inventaire
                        <span className="pdv-th-dim">OK</span>
                      </th>
                      <th>Dernière découpe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={13} className="pdv-empty">
                          Chargement des placages découpés…
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      paginated.map((r, idx) => (
                        <tr key={`${r.lot}-${r.pn}-${idx}`}>
                          <td className="pdv-lot">{r.lot || "—"}</td>
                          <td className="pdv-pn">{r.pn || "—"}</td>
                          <td className="pdv-qty">{r.epaisseur != null ? fmtQty(r.epaisseur) : "—"}</td>
                          <td className="pdv-qty-main">{fmtQty(r.placages)}</td>
                          <td className="pdv-qty">
                            <strong>{fmtQty(r.simpleOk)}</strong>
                            {Number(r.simpleNok) > 0 && (
                              <span className="pdv-nok-inline">+{fmtQty(r.simpleNok)} NOK</span>
                            )}
                          </td>
                          <td className="pdv-qty">
                            <strong>{fmtQty(r.doubleOk)}</strong>
                            {Number(r.doubleNok) > 0 && (
                              <span className="pdv-nok-inline">+{fmtQty(r.doubleNok)} NOK</span>
                            )}
                          </td>
                          <td className="pdv-qty">
                            <strong>{fmtQty(r.tripleOk)}</strong>
                            {Number(r.tripleNok) > 0 && (
                              <span className="pdv-nok-inline">+{fmtQty(r.tripleNok)} NOK</span>
                            )}
                          </td>
                          <td className="pdv-qty-ok">{fmtQty(r.totalOk)}</td>
                          <td className="pdv-qty-nok">{fmtQty(r.totalNok)}</td>
                          <td className="pdv-qty-main">{fmtVolume(r.volumeOkM3)}</td>
                          <td className="pdv-qty">{fmtPrix(r.prixMetreCube)}</td>
                          <td className="pdv-qty-ok">{fmtPrix(r.prixOk)}</td>
                          <td className="pdv-date">{formatDateTime(r.lastDate)}</td>
                        </tr>
                      ))}

                    {!loading && view.length === 0 && (
                      <tr>
                        <td colSpan={13} className="pdv-empty">
                          {search
                            ? "Aucun lot ne correspond à cette recherche"
                            : "Aucun placage découpé enregistré"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && view.length > 0 && (
                <div className="pdv-pagination">
                  <button
                    type="button"
                    className="pdv-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    ← Préc.
                  </button>
                  <span className="pdv-page-info">
                    Page {currentPage} / {totalPages}
                    <em>
                      {startIndex + 1}–{Math.min(startIndex + itemsPerPage, view.length)} sur{" "}
                      {view.length}
                    </em>
                  </span>
                  <button
                    type="button"
                    className="pdv-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Suiv. →
                  </button>
                </div>
              )}
            </section>

            {err && <div className="pdv-error">⚠ {err}</div>}
          </div>
        </div>
      </div>

      <Style />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .pdv-page {
        min-height: 100vh;
        width: 100%;
        background: radial-gradient(ellipse at top, #01283a 0%, #010F14 70%);
        color: #fff;
        font-family: Inter, "Segoe UI", system-ui, sans-serif;
      }
      .pdv-wrap {
        width: 100%;
        box-sizing: border-box;
        padding: 24px 48px 48px;
      }
      .pdv-shell {
        width: 100%;
        background: #011e2b;
        border: 1px solid #1a4a5a;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 12px 48px rgba(0,0,0,0.45);
      }
      .pdv-header {
        background: linear-gradient(135deg, #E6FF3E, #c8e800);
        color: #000;
        padding: 28px 40px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
      }
      .pdv-header-kicker {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        opacity: 0.65;
      }
      .pdv-header-title {
        margin: 0;
        font-size: 28px;
        font-weight: 800;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      .pdv-header-sub {
        margin: 0;
        font-size: 14px;
        font-weight: 500;
        opacity: 0.7;
      }
      .pdv-body {
        padding: 24px 32px 36px;
        display: flex;
        flex-direction: column;
        gap: 20px;
      }
      .pdv-stats-bar {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
      }
      .pdv-stat-pill {
        display: flex;
        flex-direction: column;
        gap: 2px;
        background: #012836;
        border: 1px solid #1a5a6a;
        border-radius: 12px;
        padding: 12px 18px;
        min-width: 160px;
      }
      .pdv-stat-label {
        font-size: 11px;
        color: #7ab8cc;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        font-weight: 700;
      }
      .pdv-stat-value {
        font-size: 24px;
        color: #E6FF3E;
        font-weight: 800;
      }
      .pdv-stat-nok .pdv-stat-value { color: #ff9090; }
      .pdv-section {
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: 14px;
        padding: 18px 20px;
      }
      .pdv-section-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 14px;
        flex-wrap: wrap;
      }
      .pdv-section-label {
        font-size: 13px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #7ab8cc;
        margin-bottom: 14px;
      }
      .pdv-section-head .pdv-section-label { margin-bottom: 0; }
      .pdv-export-btn {
        background: #E6FF3E;
        color: #011820;
        border: none;
        border-radius: 10px;
        padding: 10px 16px;
        font-size: 13px;
        font-weight: 800;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        cursor: pointer;
        transition: opacity 0.15s, transform 0.15s;
      }
      .pdv-export-btn:hover:not(:disabled) {
        transform: translateY(-1px);
        opacity: 0.92;
      }
      .pdv-export-btn:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
      .pdv-search {
        width: 100%;
        max-width: 420px;
        background: #011820;
        color: #dff8ff;
        border: 1px solid #1a4a5a;
        border-radius: 10px;
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 600;
        outline: none;
        transition: border 0.2s;
      }
      .pdv-search:focus { border-color: #E6FF3E; }
      .pdv-table-wrap {
        overflow-x: auto;
        border-radius: 10px;
        border: 1px solid #1a4a5a;
      }
      .pdv-table {
        width: 100%;
        border-collapse: collapse;
      }
      .pdv-table th {
        background: #013040;
        color: #E6FF3E;
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 12px 10px;
        text-align: left;
        border-bottom: 1px solid #1a4a5a;
        white-space: nowrap;
        vertical-align: top;
      }
      .pdv-th-dim {
        display: block;
        margin-top: 2px;
        font-size: 10px;
        font-weight: 600;
        color: #7ab8cc;
        text-transform: none;
        letter-spacing: 0.02em;
      }
      .pdv-table td {
        padding: 12px 10px;
        border-bottom: 1px solid #123848;
        font-size: 13px;
        color: #dff8ff;
        vertical-align: middle;
        background: #011820;
      }
      .pdv-table tbody tr:hover td { background: #012836; }
      .pdv-lot {
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        font-size: 13px;
        font-weight: 700;
        color: #E6FF3E;
        white-space: nowrap;
      }
      .pdv-pn {
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        font-size: 12px;
        color: #aaffcc;
        word-break: break-all;
      }
      .pdv-qty-main { font-weight: 800; font-size: 15px; color: #fff; }
      .pdv-qty strong { color: #dff8ff; font-size: 14px; }
      .pdv-nok-inline {
        display: block;
        color: #ff9090;
        font-size: 10px;
        font-weight: 700;
      }
      .pdv-qty-ok { color: #7aff9a; font-weight: 800; font-size: 14px; }
      .pdv-qty-nok { color: #ff9090; font-weight: 800; font-size: 14px; }
      .pdv-date {
        color: #7ab8cc;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
      .pdv-empty {
        text-align: center;
        padding: 28px 12px !important;
        color: #7ab8cc !important;
        font-weight: 600;
      }
      .pdv-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 14px;
        margin-top: 14px;
        flex-wrap: wrap;
      }
      .pdv-page-btn {
        background: #012836;
        color: #E6FF3E;
        border: 1px solid #1a4a5a;
        border-radius: 8px;
        padding: 8px 14px;
        font-weight: 700;
        font-size: 12px;
        cursor: pointer;
      }
      .pdv-page-btn:hover:not(:disabled) { background: #1a4a5a; color: #fff; }
      .pdv-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .pdv-page-info {
        color: #dff8ff;
        font-weight: 700;
        font-size: 13px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
      }
      .pdv-page-info em {
        font-style: normal;
        color: #5a8a99;
        font-size: 11px;
        font-weight: 600;
      }
      .pdv-error {
        background: #2a0808;
        border: 1px solid #C00000;
        color: #ff9090;
        border-radius: 10px;
        padding: 12px 14px;
        font-weight: 600;
      }
      @media (max-width: 768px) {
        .pdv-wrap { padding: 16px; }
        .pdv-header { padding: 20px 24px; }
        .pdv-header-title { font-size: 20px; }
        .pdv-body { padding: 16px 16px 24px; }
        .pdv-stat-pill { min-width: 120px; }
      }
    `}</style>
  );
}
